import * as React from 'react';
import {
  Button,
  CloseDefaultIcon,
  HStack,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  VStack,
} from '@packages/systems/spring';
import {
  CODE_COMPONENT_TEMPLATES,
  getCodeComponentTemplate,
  type CodeComponentTemplate,
  type CodeComponentTemplateId,
} from '@packages/systems/code-libraries/core';
import {useFeatureFlag} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {usePrefersReducedMotion} from '@packages/systems/spring-hooks';
import {emotionStyled} from '@packages/utilities/styled';
import multistepFormIcon from './assets/multistep-form-icon.svg?resource';
import pricingCalculatorIcon from './assets/pricing-calculator-icon.svg?resource';
import quotesCarouselIcon from './assets/quotes-carousel-icon.svg?resource';
import shaderIcon from './assets/shader-icon.svg?resource';
import {CODE_COMPONENT_TEMPLATE_VIDEOS} from './codeComponentTemplateVideos';

export const CodeComponentTemplatePickerAnchorContext =
  React.createContext<React.ComponentProps<typeof Popover>['anchorRef']>(null);

type CodeComponentTemplatePickerProps = {
  children?: React.ReactElement<{onClick?: React.MouseEventHandler}>;
  templateId?: CodeComponentTemplateId;
  returnFocusTo?: HTMLElement | null;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  onCreate: (templateId?: CodeComponentTemplateId) => void;
};

// The picker promotes a single template; showing several needs its own design.
const [defaultTemplate] = CODE_COMPONENT_TEMPLATES;

const PreviewVideo = emotionStyled('video')({
  display: 'block',
  objectFit: 'cover',
});

export type AddPanelCodeComponentTemplate = Exclude<
  CodeComponentTemplate,
  {id: 'locations-map'}
>;

// The Add panel already has a native Map element, so the map template only
// appears through its nudge.
export const addPanelCodeComponentTemplates = CODE_COMPONENT_TEMPLATES.filter(
  (template): template is AddPanelCodeComponentTemplate =>
    template.id !== 'locations-map'
);

export const codeComponentTemplateIcons: Record<
  AddPanelCodeComponentTemplate['id'],
  string
> = {
  'pricing-calculator': pricingCalculatorIcon,
  shader: shaderIcon,
  'quotes-carousel': quotesCarouselIcon,
  'multistep-form': multistepFormIcon,
};

export function CodeComponentTemplatePicker({
  children,
  templateId,
  returnFocusTo,
  isOpen,
  onOpenChange,
  onCreate,
}: CodeComponentTemplatePickerProps) {
  const panelAnchor = React.useContext(
    CodeComponentTemplatePickerAnchorContext
  );
  const [internalIsOpen, setInternalIsOpen] = React.useState(false);
  const areTemplatesEnabled = useFeatureFlag(
    FEATURE_FLAGS.AICC_COMPONENT_TEMPLATES
  );
  const prefersReducedMotion = usePrefersReducedMotion();
  const template = getCodeComponentTemplate(templateId) ?? defaultTemplate;
  const resolvedIsOpen = isOpen ?? internalIsOpen;
  const setIsOpen = (open: boolean) => {
    setInternalIsOpen(open);
    onOpenChange?.(open);
  };

  if (!areTemplatesEnabled) {
    // Without templates the trigger creates a blank component directly.
    return children
      ? React.cloneElement(children, {
          onClick: (event) => {
            children.props.onClick?.(event);
            onCreate();
          },
        })
      : null;
  }

  return (
    <Popover
      isOpen={resolvedIsOpen}
      onOpenChange={setIsOpen}
      anchorRef={panelAnchor}
      returnFocus={returnFocusTo ? {current: returnFocusTo} : true}
      placement={panelAnchor ? 'right-end' : 'right-start'}
      horizontalOffset={panelAnchor ? 20 : undefined}
      shouldEscapeViewport={Boolean(panelAnchor)}
      width={281}
    >
      {children && (
        <PopoverTrigger onClick={() => setIsOpen(!resolvedIsOpen)}>
          {children}
        </PopoverTrigger>
      )}
      <PopoverContent
        colorVariant="blue"
        isSeamless
        aria-label="Code component templates"
        data-automation-id="code-component-template-picker"
      >
        <VStack spacing={2} padding="$3" width={281} minWidth={0}>
          <HStack align="start" spacing={2}>
            <Text weight="semibold" flex={1}>
              {template.picker.heading}
            </Text>
            <IconButton
              Icon={CloseDefaultIcon}
              label="Close templates"
              colorVariant="white"
              variant="ghost"
              density="compact"
              onClick={() => setIsOpen(false)}
            />
          </HStack>
          <PreviewVideo
            autoPlay={!prefersReducedMotion}
            // Without autoplay the clip needs a way to be watched.
            controls={prefersReducedMotion}
            // Remount when the template changes so the element loads the new source.
            key={template.id}
            loop
            muted
            playsInline
            preload="metadata"
            src={CODE_COMPONENT_TEMPLATE_VIDEOS[template.id]}
            title={`${template.name} preview`}
            width={257}
            height={124}
          />
          <Text>{template.picker.body}</Text>
          <HStack>
            <Button
              colorVariant="white"
              variant="outline"
              onClick={() => {
                setIsOpen(false);
                onCreate(template.id);
              }}
              data-automation-id={`code-component-template-${template.id}`}
            >
              Create component
            </Button>
          </HStack>
        </VStack>
      </PopoverContent>
    </Popover>
  );
}
